/**
 * Feature Access Guard Components and Utilities
 *
 * Reusable components and utilities for feature-gating UI elements
 */

import React from "react";
import type { ReactNode } from "react";
import { useFeatureAccess } from "../hooks/useFeatureAccess";
import { cn } from "../utils/cn";

interface FeatureLockedProps {
  featureId: string;
  /** The content to show if locked */
  children: ReactNode;
  /** Optional alternative content to show (e.g., upgrade button) */
  fallback?: ReactNode;
  /** Optional blur intensity (0-1) */
  blurIntensity?: number;
}

/**
 * Component that blurs content and shows an upgrade CTA overlay
 * Used for content that's feature-locked
 */
export const FeatureLockedContent: React.FC<FeatureLockedProps> = ({
  featureId,
  children,
  fallback,
  blurIntensity = 0.5,
}) => {
  const { hasFeature } = useFeatureAccess();

  if (hasFeature(featureId)) {
    return <>{children}</>;
  }

  return (
    <div
      className="feature-locked-container"
      id={`feature-locked-${featureId}`}
    >
      <div
        className="feature-locked-blur"
        style={{ filter: `blur(${blurIntensity * 8}px)` }}
      >
        {children}
      </div>
      <div className="feature-locked-overlay">
        {fallback || (
          <div className="feature-locked-content">
            <p className="feature-locked-title">🔒 Premium Feature</p>
            <p className="feature-locked-description">
              Unlock advanced tools to refine your retirement plan
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

interface FeatureDisabledButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  featureId: string;
  children: ReactNode;
  /** Text to show on hover when disabled */
  disabledHint?: string;
}

/**
 * Button component that's disabled if user doesn't have feature access
 * Shows a tooltip hint on hover when disabled
 */
export const FeatureDisabledButton = React.forwardRef<
  HTMLButtonElement,
  FeatureDisabledButtonProps
>(
  (
    { featureId, children, disabledHint = "Upgrade to unlock", ...props },
    ref
  ) => {
    const { hasFeature } = useFeatureAccess();
    const hasAccess = hasFeature(featureId);

    return (
      <button
        ref={ref}
        disabled={!hasAccess}
        title={!hasAccess ? disabledHint : undefined}
        className={cn(
          props.className,
          !hasAccess && "cursor-not-allowed opacity-50"
        )}
        {...props}
      >
        {children}
      </button>
    );
  }
);

FeatureDisabledButton.displayName = "FeatureDisabledButton";

interface FeatureGateProps {
  featureId: string;
  /** Content to render if user has access */
  children: ReactNode;
  /** Content to render if user doesn't have access */
  fallback?: ReactNode;
}

/**
 * Conditional render based on feature access
 *
 * @example
 * ```tsx
 * <FeatureGate featureId="feature-custom-assumptions">
 *   <CustomAssumptionsForm />
 *   <UnlockPrompt slot="fallback" />
 * </FeatureGate>
 * ```
 */
export const FeatureGate: React.FC<FeatureGateProps> = ({
  featureId,
  children,
  fallback,
}) => {
  const { hasFeature } = useFeatureAccess();

  if (hasFeature(featureId)) {
    return <>{children}</>;
  }

  if (fallback) {
    return <>{fallback}</>;
  }

  return null;
};
